import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  EnrollmentEarnings,
  getEarnings,
  getEarningsMilestones,
  getLedger,
  getPaymentTerms,
  MilestoneEarningRow,
  PaymentTerms,
  TeacherEarnings as TeacherEarningsModel,
  TeacherLedger,
} from '../api/earnings';
import PendingConfirmations from '../components/PendingConfirmations';
import { activatable } from '../../../commodity/components/ui/activatable';
import { formatMoney } from '../../../commodity/format/money';
import { PortalPage } from '../../../design-system/portal';
import { closingLabel } from '../vocabulary/learningVocabulary';

const fmt = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
};

const Stat: React.FC<{ label: string; value: string; color: string }> = ({
  label,
  value,
  color,
}) => (
  <div
    style={{
      border: '1px solid #d1d5da',
      borderRadius: 8,
      padding: 16,
      minWidth: 140,
      flex: 1,
    }}
  >
    <div style={{ fontSize: 12, color: '#666' }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
  </div>
);

/**
 * A rate as a percentage.
 *
 * The value always comes from the API, which reads the constants the escrow
 * applies. Writing "25%" here would be a second copy of a number that can
 * change, and the copy on screen is the one nobody notices going stale.
 */
const pct = (rate: number): string => `${Math.round(rate * 100)}%`;

/**
 * MC-03 — a session the teacher did not give reads as refunded to the family,
 * never as pending or paid.
 */
const milestoneStatusLabel = (m: MilestoneEarningRow): string => {
  if (!m.notDelivered) return m.status;
  return m.refundedAt
    ? 'No impartida · devuelta a la familia'
    : 'No impartida · devolución en proceso';
};

const TeacherEarnings: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<TeacherEarningsModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [rows, setRows] = useState<Record<string, MilestoneEarningRow[]>>({});
  const [ledger, setLedger] = useState<TeacherLedger | null>(null);
  const [ledgerError, setLedgerError] = useState<unknown>(undefined);
  const [terms, setTerms] = useState<PaymentTerms | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Settled rather than sequential: the summary and the history fail
      // independently, and a teacher whose history is unavailable should
      // still see what they are owed. The rejection is recorded, not
      // defaulted away — an empty ledger and an unreachable one are
      // different facts (S1.3).
      const [earningsRes, ledgerRes, termsRes] = await Promise.allSettled([
        getEarnings(),
        getLedger(),
        getPaymentTerms(),
      ]);
      if (cancelled) return;

      // Terms are explanatory, so a failure hides the explanation rather than
      // the money. The rejection is still recorded, not defaulted to a shape
      // the screen would then draw numbers from.
      setTerms(termsRes.status === 'fulfilled' ? termsRes.value.terms : null);

      if (earningsRes.status === 'fulfilled') {
        setData(earningsRes.value.earnings);
      } else {
        setError((earningsRes.reason as Error)?.message || 'No se pudieron cargar los ingresos');
      }
      setLedger(ledgerRes.status === 'fulfilled' ? ledgerRes.value.ledger : null);
      setLedgerError(ledgerRes.status === 'rejected' ? ledgerRes.reason : undefined);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = async (e: EnrollmentEarnings) => {
    if (openId === e.enrollmentId) {
      setOpenId(null);
      return;
    }
    setOpenId(e.enrollmentId);
    if (!rows[e.enrollmentId]) {
      try {
        const res = await getEarningsMilestones(e.enrollmentId);
        setRows((r) => ({ ...r, [e.enrollmentId]: res.milestones }));
      } catch {
        setRows((r) => ({ ...r, [e.enrollmentId]: [] }));
      }
    }
  };

  if (loading) return <p>Cargando tus ingresos...</p>;
  if (error) return <p style={{ color: '#c92a2a' }}>{error}</p>;
  if (!data) return <p>Sin datos de ingresos.</p>;

  return (
    <PortalPage screenId="s_revenue">
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <h1>Mis ingresos</h1>

        <PendingConfirmations
          enrollmentIds={data.enrollments.map((e) => e.enrollmentId)}
          actor="teacher"
        />

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
          <Stat
            label="Liberado"
            value={formatMoney(data.totals.releasedToDate, data.currency)}
            color="#2b8a3e"
          />
          <Stat
            label="Pendiente"
            value={formatMoney(data.totals.pending, data.currency)}
            color="#1971c2"
          />
          <Stat
            label="En retención"
            value={formatMoney(data.totals.holdbackInProgress, data.currency)}
            color="#e8590c"
          />
          <Stat
            label="En disputa"
            value={formatMoney(data.totals.disputed, data.currency)}
            color="#c92a2a"
          />
        </div>

        <h2 style={{ marginTop: 24 }}>Por inscripción</h2>
        {data.enrollments.length === 0 && (
          <p style={{ color: '#666' }}>Aún no tienes inscripciones.</p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {data.enrollments.map((e) => (
            <div key={e.enrollmentId} style={{ border: '1px solid #d1d5da', borderRadius: 8 }}>
              <div
                {...activatable(() => toggle(e), `Detalle de la inscripción ${e.enrollmentId}`)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 8,
                  padding: 14,
                  cursor: 'pointer',
                }}
              >
                <div>
                  <strong>Inscripción {e.enrollmentId.slice(0, 8)}</strong>
                  <span style={{ color: '#666', fontSize: 12, marginLeft: 8 }}>{e.state}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 13 }}>
                  <span>
                    Liberado <strong>{formatMoney(e.releasedToDate, e.currency)}</strong> ·
                    Pendiente <strong>{formatMoney(e.pending, e.currency)}</strong> · Retención{' '}
                    <strong>{formatMoney(e.holdbackInProgress, e.currency)}</strong>
                  </span>
                  <button
                    onClick={(ev) => {
                      ev.stopPropagation();
                      navigate(`/enrollments/${e.enrollmentId}`);
                    }}
                    style={{
                      border: '1px solid #1971c2',
                      background: '#fff',
                      color: '#1971c2',
                      borderRadius: 4,
                      padding: '6px 10px',
                      fontSize: 12,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Ver inscripción →
                  </button>
                </div>
              </div>

              {openId === e.enrollmentId && (
                <div style={{ borderTop: '1px solid #e9ecef', padding: 14 }}>
                  {!rows[e.enrollmentId] && <p>Cargando hitos...</p>}
                  {rows[e.enrollmentId] && rows[e.enrollmentId].length === 0 && (
                    <p style={{ color: '#666' }}>Sin hitos.</p>
                  )}
                  {rows[e.enrollmentId] && rows[e.enrollmentId].length > 0 && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ textAlign: 'left', borderBottom: '2px solid #d1d5da' }}>
                          <th style={{ padding: 6 }}>#</th>
                          <th>Monto</th>
                          <th>Estado</th>
                          <th>Liberado</th>
                          <th>Método</th>
                          <th>ID de pago</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows[e.enrollmentId].map((m) => (
                          <tr key={m.sequence} style={{ borderBottom: '1px solid #e9ecef' }}>
                            <td style={{ padding: 6 }}>{m.sequence === 8 ? 'PF' : m.sequence}</td>
                            <td>{formatMoney(m.amount, data.currency)}</td>
                            <td>{milestoneStatusLabel(m)}</td>
                            <td>{fmt(m.releasedAt)}</td>
                            <td>{m.paymentMethod ?? '—'}</td>
                            <td style={{ fontFamily: 'monospace', fontSize: 11 }}>
                              {m.releaseId ?? '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {terms && (
          <section style={{ marginTop: 28 }}>
            <h2 style={{ fontSize: 18, margin: '0 0 4px' }}>Cómo se te paga</h2>
            <p style={{ color: '#666', fontSize: 13, marginTop: 0 }}>
              {terms.platformCommissionRate === 0
                ? 'IQMaxx no se queda con ninguna comisión: recibes el precio completo del curso. Lo que cambia es cuándo.'
                : `IQMaxx retiene ${pct(terms.platformCommissionRate)} del precio del curso.`}
            </p>
            <ul style={{ color: '#333', fontSize: 14, margin: 0, paddingLeft: 18 }}>
              <li style={{ marginBottom: 4 }}>
                <strong>{pct(terms.bookingFeeRate)}</strong> al activarse la inscripción.
              </li>
              <li style={{ marginBottom: 4 }}>
                <strong>{pct(terms.sessionPoolRate)}</strong> repartido entre las{' '}
                {terms.sessionCount} sesiones, conforme las completas.
              </li>
              <li style={{ marginBottom: 4 }}>
                <strong>{pct(terms.holdbackRate)}</strong> retenido hasta {terms.holdbackHours}{' '}
                horas después del {closingLabel().toLowerCase()}.
              </li>
            </ul>
            <p style={{ color: '#666', fontSize: 12, marginBottom: 0 }}>
              Si una familia cancela con menos de {terms.cancellationCutoffHours} horas de
              anticipación, conservas {pct(terms.lateCancellationRate)} de esa sesión.
            </p>
          </section>
        )}

        <h2 style={{ fontSize: 18, margin: '28px 0 4px' }}>Movimientos</h2>
        <p style={{ color: '#666', fontSize: 13, marginTop: 0 }}>
          Cada liberación, en el orden en que ocurrió, con su referencia de pago.
        </p>

        {ledgerError ? (
          <p style={{ color: '#c92a2a' }}>
            No pudimos cargar tus movimientos. El resumen de arriba sí está actualizado.
          </p>
        ) : !ledger ? null : ledger.entries.length === 0 ? (
          <p style={{ color: '#666' }}>Todavía no se ha liberado ningún pago.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#666' }}>
                <th style={{ padding: 6 }}>Fecha</th>
                <th>Concepto</th>
                <th>Monto</th>
                <th>Método</th>
                <th>ID de pago</th>
              </tr>
            </thead>
            <tbody>
              {ledger.entries.map((entry) => (
                <tr
                  key={`${entry.enrollmentId}-${entry.sequence}-${entry.at}`}
                  style={{ borderBottom: '1px solid #e9ecef' }}
                >
                  <td style={{ padding: 6 }}>{fmt(entry.at)}</td>
                  <td>
                    {entry.kind === 'FAMILY_REFUND'
                      ? `Devolución a la familia · Sesión ${
                          entry.sequence ?? '—'
                        } no impartida (no se descuenta de tu saldo)`
                      : entry.kind === 'REFUND'
                      ? `Reembolso${entry.reason ? ` · ${entry.reason}` : ''}`
                      : entry.milestoneType === 'final_project'
                      ? closingLabel()
                      : `Sesión ${entry.sequence}`}{' '}
                    <span style={{ color: '#868e96' }}>
                      · Inscripción {entry.enrollmentId.slice(0, 8)}
                    </span>
                  </td>
                  <td
                    style={{
                      color:
                        entry.kind === 'FAMILY_REFUND'
                          ? '#868e96'
                          : entry.amount < 0
                          ? '#c92a2a'
                          : undefined,
                    }}
                  >
                    {formatMoney(entry.amount, ledger.currency)}
                  </td>
                  <td>{entry.paymentMethod ?? '—'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>
                    {entry.reference ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/*
          What the history cannot place on a date. Shown rather than dropped:
          a total that silently disagrees with settlement is worse than one that
          says which part it cannot itemise.
        */}
        {ledger &&
          (ledger.unplaced.refundsWithoutRecord.amount > 0 ||
            ledger.unplaced.undatedReleases.count > 0) && (
            <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
              {ledger.unplaced.refundsWithoutRecord.amount > 0 && (
                <div>
                  No incluido arriba:{' '}
                  {formatMoney(ledger.unplaced.refundsWithoutRecord.amount, ledger.currency)} en
                  reembolsos anteriores a nuestro registro individual, así que no podemos fecharlos.
                </div>
              )}
              {ledger.unplaced.undatedReleases.count > 0 && (
                <div>
                  No incluido arriba:{' '}
                  {formatMoney(ledger.unplaced.undatedReleases.amount, ledger.currency)} liberados
                  sin fecha registrada.
                </div>
              )}
            </div>
          )}
      </div>
    </PortalPage>
  );
};

export default TeacherEarnings;
